import React, { useMemo } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';

import { OCardCarousel as OCardCarouselComp } from '@marriott/mi-web-ui-kit';
import clsx from 'clsx';

export const OCardCarouselConfig = {
  emptyLabel: 'OCardCarousel',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/o-card-carousel`,
};

// Reads `key` from props directly or from props.model (mirrors OTabContainer pattern).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getFromAnywhere(props: any, key: string) {
  if (props?.[key] !== undefined) return props[key];
  if (props?.model?.[key] !== undefined) return props.model[key];
  return undefined;
}

/**
 * Hoists AEM model fields to top-level props so OCardCarouselComp can destructure
 * them regardless of whether AEM delivered them flat or nested inside `model`.
 *
 * itemPath derivation rules (in priority order):
 *  1. model.itemPath  — the correct relative path AEM writes into the model
 *                       (e.g. "root/responsivegrid/.../o_card_carousel").
 *  2. props.cqPath stripped of its pagePath/jcr:content/ prefix — fallback
 *     for environments where model.itemPath is absent. props.cqPath is the
 *     FULL absolute path injected by the AEM SPA framework; using it directly
 *     as itemPath causes MSlot to double-prefix the path when computing the
 *     ResponsiveGrid registration path.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function enrichCarouselProps(props: any) {
  const noOfTotalCards = getFromAnywhere(props, 'noOfTotalCards');
  const childComponentResourceType = getFromAnywhere(props, 'childComponentResourceType');
  const pagePath = getFromAnywhere(props, 'pagePath') as string | undefined;

  // model.itemPath is the correct relative JCR path (e.g.
  // "root/responsivegrid/responsivegrid_hero/o_card_carousel").
  // props.cqPath is the FULL absolute path injected by the AEM SPA framework
  // (pagePath + /jcr:content/ + relativeItemPath). Using it directly as
  // itemPath causes MSlot to prepend pagePath/jcr:content/ a second time.
  // Prefer model.itemPath; derive from cqPath only as a last resort.
  const itemPathFromProps = getFromAnywhere(props, 'itemPath') as string | undefined;
  const cqPathFull = props?.['cqPath'] as string | undefined;
  let itemPath: string | undefined;
  if (itemPathFromProps !== undefined) {
    // model.itemPath present — use it directly (already relative).
    itemPath = itemPathFromProps;
  } else if (cqPathFull && pagePath) {
    // Strip "pagePath/jcr:content/" prefix to get the relative path.
    const jcrPrefix = `${pagePath}/jcr:content/`;
    itemPath = cqPathFull.startsWith(jcrPrefix) ? cqPathFull.slice(jcrPrefix.length) : cqPathFull;
  } else {
    itemPath = cqPathFull;
  }

  // ':items', ':itemsOrder', cqItems, cqItemsOrder carry authored child data
  // and may arrive nested inside model when AEM injects via MapTo.
  const aemItems = getFromAnywhere(props, ':items');
  const aemItemsOrder = getFromAnywhere(props, ':itemsOrder');
  const cqItems = getFromAnywhere(props, 'cqItems');
  const cqItemsOrder = getFromAnywhere(props, 'cqItemsOrder');

  // isAuthorMode and aemReactComponentMapping are injected by import-components.js
  // at top-level only — never inside model.
  const isAuthorMode = props?.['isAuthorMode'];
  const aemReactComponentMapping = props?.['aemReactComponentMapping'];

  const patch: Record<string, unknown> = {};
  if (noOfTotalCards !== undefined) patch['noOfTotalCards'] = noOfTotalCards;
  if (childComponentResourceType !== undefined) patch['childComponentResourceType'] = childComponentResourceType;
  if (pagePath !== undefined) patch['pagePath'] = pagePath;
  if (itemPath !== undefined) patch['itemPath'] = itemPath;
  if (aemItems !== undefined) patch[':items'] = aemItems;
  if (aemItemsOrder !== undefined) patch[':itemsOrder'] = aemItemsOrder;
  if (cqItems !== undefined) patch['cqItems'] = cqItems;
  if (cqItemsOrder !== undefined) patch['cqItemsOrder'] = cqItemsOrder;
  if (isAuthorMode !== undefined) patch['isAuthorMode'] = isAuthorMode;
  if (aemReactComponentMapping !== undefined) patch['aemReactComponentMapping'] = aemReactComponentMapping;
  if (!Object.keys(patch).length) return props;
  return { ...props, ...patch };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OCardCarousel = (props: any) => {
  const model = props.model || {};
  const { styleclass, componentId } = model;

  const enrichedProps = useMemo(() => enrichCarouselProps(props), [props]);

  const hasChildComponentResourceType = Boolean(enrichedProps['childComponentResourceType']);

  // In author mode, AEM's ModelManager may inject model fields one render cycle
  // after mount for non-first carousel instances. Guard: keep EditableComponent
  // mounted immediately (AEM overlay always present) but defer OCardCarouselComp
  // until at least one model field confirms the model has been injected.
  // Not needed in publish mode — SSR provides all props upfront.
  const isAuthorMode = Boolean(enrichedProps['isAuthorMode']);
  const aemItemsObj = enrichedProps[':items'] as Record<string, unknown> | undefined;
  const modelReady =
    enrichedProps['noOfTotalCards'] !== undefined ||
    enrichedProps['childComponentResourceType'] !== undefined ||
    Boolean(enrichedProps['cqItems'] && Object.keys(enrichedProps['cqItems'] as Record<string, unknown>).length > 0) ||
    Boolean(aemItemsObj && Object.keys(aemItemsObj).length > 0);

  return (
    <div
      data-testid="o-card-carousel-comp"
      data-component-name="o-card-carousel"
      id={componentId}
      className={clsx(styleclass)}
    >
      {hasChildComponentResourceType ? (
        <EditableComponent config={OCardCarouselConfig} {...enrichedProps}>
          {(!isAuthorMode || modelReady) && <OCardCarouselComp {...enrichedProps} />}
        </EditableComponent>
      ) : (
        <EditableComponent config={OCardCarouselConfig} {...props}>
          <OCardCarouselComp {...props} />
        </EditableComponent>
      )}
    </div>
  );
};
